// Compact identity strip for a resolved bundle. Renders filename,
// short sha (click-to-copy), and humanized size. Shared between the
// operator and data-confident variants — the refined variant keeps
// this information inline within its inspection section instead.

import { formatBytes } from "@veil/ui/lib/format";
import { Check, Copy } from "lucide-react";
import { useState } from "react";

interface Props {
	filename: string;
	sha256: string;
	sizeBytes: number;
	className?: string;
}

export function ShaStrip({ filename, sha256, sizeBytes, className }: Props) {
	const [copied, setCopied] = useState(false);

	const onCopy = async () => {
		try {
			await navigator.clipboard.writeText(sha256);
			setCopied(true);
			setTimeout(() => setCopied(false), 1200);
		} catch {
			// silent: clipboard write blocked, nothing actionable to surface.
		}
	};

	return (
		<div
			className={`flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-xs ${className ?? ""}`}
		>
			<span className="truncate text-foreground/90">{filename}</span>
			<button
				type="button"
				onClick={onCopy}
				title="Copy sha256"
				className="group inline-flex items-center gap-1.5 rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
			>
				<span className="tabular-nums">
					sha256:{sha256.slice(0, 12)}
					<span className="text-muted-foreground/50">…</span>
				</span>
				{copied ? (
					<Check className="size-3 text-emerald-500" />
				) : (
					<Copy className="size-3 opacity-0 transition-opacity group-hover:opacity-100" />
				)}
			</button>
			<span className="ml-auto tabular-nums text-muted-foreground">
				{formatBytes(sizeBytes)}
			</span>
		</div>
	);
}
